import React, { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import { FadeIn, StaggeredEntrance } from '@atlaskit/motion';
import { Stack } from '@atlaskit/primitives/compiled';
import {
	type ConversationStarter,
	getConversationStarters,
} from '@atlaskit/rovo-agent-components/ui/AgentConversationStarters';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { DynamicSurfaceActionItem } from '../../common/ui/action-item';

const styles = cssMap({
	startersContainer: {
		width: '100%',
	},
});

type RovoAgentConversationStartersProps = {
	agentId: string;
	conversationStarters: ReadonlyArray<string> | null | undefined;
	onConversationStarterClick: (conversationStarter: ConversationStarter) => void;
};

export const RovoAgentConversationStarters = ({
	agentId,
	conversationStarters: userDefinedConversationStarters,
	onConversationStarterClick,
}: RovoAgentConversationStartersProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const conversationStarters = useMemo(() => {
		return getConversationStarters({
			userDefinedConversationStarters: userDefinedConversationStarters?.map((starter) => {
				return {
					message: starter,
					type: 'user-defined',
				};
			}),
			isAgentDefault: false,
		}).combinedConversationStarters.map((starter) =>
			typeof starter.message === 'string'
				? { message: starter.message, type: starter.type }
				: { message: formatMessage(starter.message), type: starter.type },
		);
	}, [formatMessage, userDefinedConversationStarters]);

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'newRovoChatAgentConversation',
			attributes: {
				agentId,
			},
		});
		// Should only fire on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [agentId]);

	return (
		<Stack xcss={styles.startersContainer} space="space.050">
			{conversationStarters.length > 0 && (
				<StaggeredEntrance columns={1}>
					{conversationStarters.map((starter, index) => (
						<FadeIn key={`conversation-starter-${index}`}>
							{(props) => (
								<div {...props}>
									<DynamicSurfaceActionItem
										title={starter.message}
										icon={() => <RovoChatIcon label="" />}
										onClick={() => onConversationStarterClick(starter)}
										testId={`rovo-agent-conversation-starter-${index}`}
									/>
								</div>
							)}
						</FadeIn>
					))}
				</StaggeredEntrance>
			)}
		</Stack>
	);
};
